import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'

import { ApiError } from '@/lib/api/http-client'
import { hasPortalSession } from '@/lib/auth/portal-routes'

import { getWecomBindingSession, resumeWecomBinding } from './api'

const terminalStatuses = new Set(['completed', 'conflict', 'expired'])

/** Bot 只进入共享企微 Auth；返回后的身份匹配由后端验证，不信任 URL 标记。 */
export function useWecomBindingPage() {
  const params = useParams({ strict: false })
  const bindingToken = typeof params.bindingToken === 'string' ? params.bindingToken : ''
  const resumed = useRef(false)
  const resumeRequested = new URLSearchParams(window.location.search).get('wecom_auth') === '1'
  const hasUserSession = hasPortalSession('user')
  const sessionQuery = useQuery({
    enabled: Boolean(bindingToken),
    queryFn: () => getWecomBindingSession(bindingToken),
    queryKey: ['wecom-binding-session', bindingToken],
    retry: false,
  })
  const resume = useMutation({ mutationFn: () => resumeWecomBinding(bindingToken) })
  const { mutate } = resume
  useEffect(() => {
    if (
      resumeRequested &&
      hasUserSession &&
      sessionQuery.data &&
      !terminalStatuses.has(sessionQuery.data.status) &&
      !resumed.current
    ) {
      resumed.current = true
      mutate()
    }
  }, [resumeRequested, hasUserSession, sessionQuery.data, mutate])
  const error = resume.error ?? sessionQuery.error
  const session = resume.data ?? sessionQuery.data
  const errorCode =
    error instanceof ApiError
      ? error.errorCode
      : error
        ? 'binding_unavailable'
        : session?.error_code
  return {
    session,
    errorCode,
    loading: sessionQuery.isLoading || resume.isPending,
    retry: () => (resume.isError ? resume.mutate() : void sessionQuery.refetch()),
    authorize: () => {
      if (session?.authorize_url) window.location.assign(session.authorize_url)
    },
  }
}
